import { useQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useMemo, useState } from "react";

import TimeLogFilters from "@components/forms/TimeLogFilters";
import HandleQuery from "@components/HandleQuery";
import ExpertTablesPage from "@components/pageTypes/ExpertTablesPage";
import PageTitle from "@components/text/PageTitle";
import TimeLogTables from "@components/TimeLogTables";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import {
  ExpertGlobalRoleEnum,
  GetTimeLogsDocument,
  GetTimeLogsQuery,
  GetTimeLogsQueryVariables,
  TimeLogBillableStatusEnum
} from "@typing/Generated";

type Filters = ComponentProps<typeof TimeLogFilters>["filters"];

const ExpertTimeLogsPage = () => {
  const myExpert = useMyExpert();

  const [filters, setFilters] = useState<Filters>({ billable: TimeLogBillableStatusEnum.all, expertId: "" });
  const useExpertId = useMemo(() => {
    if (myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager) {
      return filters.expertId;
    }
    return myExpert?.id;
  }, [filters.expertId, myExpert?.id, myExpert?.expertGlobalRole]);

  const getTimeLogsQuery = useQuery<GetTimeLogsQuery, GetTimeLogsQueryVariables>(GetTimeLogsDocument, {
    variables: { billable: filters.billable, expertId: useExpertId }
  });

  const timeLogs = useNodesFromConnection(getTimeLogsQuery.data?.timeLogs);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.timeLog.list">
      <PageTitle imageName="map" titleKey="pages.expert.timeLogs.title" />

      <TimeLogFilters filters={filters} onChange={handleFilterChange} />
      <HandleQuery query={getTimeLogsQuery}>
        {_data => (
          <TimeLogTables
            fetchMore={getTimeLogsQuery.fetchMore}
            pageInfo={getTimeLogsQuery.data?.timeLogs.pageInfo}
            refetch={getTimeLogsQuery.refetch}
            timeLogs={timeLogs}
          />
        )}
      </HandleQuery>
    </ExpertTablesPage>
  );
};

export default ExpertTimeLogsPage;
